"use client";

import dayjs from "dayjs";
import React from "react";
import useMeasure from "react-use-measure";
import uPlot from "uplot";
import UplotReact from "uplot-react";

import { enNames, zhNames } from "@/utils/uplot-plugins/constant";
import { uplotTooltipPlugin } from "@/utils/uplot-plugins/tooltip-plugin";

import { UInsiderChartProps } from "./UInsiderChartProps";
import { getDiscreteColor } from "./utils/discrete-color";
import { drawZeroLine } from "./utils/draw-zero-line";
import { insiderTooltipContent } from "./utils/insider-tooltip-content";

const scaleStops = [
  { lowerBound: -Infinity, color: "#F1A958E6" },
  { lowerBound: 0, color: "#5A8FD6E6" },
];

const getLegends = (lang: "en" | "zh") => [
  {
    label: lang === "en" ? "Shares of Buy" : "淨買入股數",
    color: "#5A8FD6",
  },
  {
    label: lang === "en" ? "Shares of Sell" : "淨賣出股數",
    color: "#F1A958",
  },
  { label: lang === "en" ? "Close Price" : "收盤價", color: "#225A9F" },
];

const UInsiderChart = ({
  data,
  lang = "zh",
  tradeUnit = "K",
}: UInsiderChartProps) => {
  const [ref, { width: fullWidth, height: fullHeight }] = useMeasure();

  if (!data.length) return <></>;
  const minVal = Math.min(...(data[2] as number[]));
  const maxVal = Math.max(...(data[2] as number[]));
  const legends = getLegends(lang);

  return (
    <div className="h-full w-full" ref={ref}>
      <div className="flex h-full w-full flex-col">
        <div className="flex justify-between pl-4">
          <p className="text-sm">{lang === "en" ? "Shares" : "股數"}</p>
          <p className="text-sm">{lang === "en" ? "Close Price" : "收盤價"}</p>
        </div>
        <UplotReact
          data={data}
          options={{
            width: fullWidth,
            height: fullHeight,
            cursor: { y: false },
            fmtDate: (tpl) =>
              uPlot.fmtDate(tpl, lang === "en" ? enNames : zhNames),
            series: [
              {
                value: (_u, v) =>
                  v ? dayjs.unix(v).format("YYYY/MM/DD") : "-",
              },
              {
                points: {
                  show: false,
                  size: 8,
                  fill: "#225A9F54",
                },
                stroke: "#225A9F",
                width: 2.5,
                scale: "$",
              },
              {
                label: tradeUnit,
                points: {
                  show: false,
                  size: 0,
                },
                stroke: (u) => getDiscreteColor({ u, scaleStops, tradeUnit }),
                fill: (u) => getDiscreteColor({ u, scaleStops, tradeUnit }),
                width: 0,
                scale: tradeUnit,
                value: (_u, v) => (v != undefined ? `${v}${tradeUnit}` : "-"),
              },
            ],
            axes: [
              { grid: { show: false }, ticks: { show: false } },
              {
                scale: "$",
                values: (_, ticks) => ticks.map((rawValue) => `$${rawValue}`),
                side: 1,
                gap: 8,
                ticks: { show: false },
                grid: { show: false },
              },
              {
                scale: tradeUnit,
                values: (_, ticks) =>
                  ticks.map((rawValue) =>
                    rawValue === 0 ? rawValue : `${rawValue}${tradeUnit}`
                  ),
                side: 3,
                gap: 8,
                grid: { show: true },
                ticks: { show: false },
              },
            ],
            scales: {
              [tradeUnit]: {
                range: () => {
                  return [minVal * 1.1, maxVal * 1.1];
                },
              },
            },
            hooks: {
              drawSeries: [
                (u, si) => {
                  if (si !== 2) return;
                  drawZeroLine({ u, si, tradeUnit });
                },
              ],
            },
            legend: { show: false },
            plugins: [
              uplotTooltipPlugin(insiderTooltipContent({ lang, tradeUnit })),
            ],
          }}
        />
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 pt-1 text-sm font-medium @lg:gap-x-10">
          {legends.map(({ label, color }) => (
            <div className="flex select-none items-center gap-1" key={label}>
              <span
                style={{ backgroundColor: color }}
                className="mt-px h-3 w-3"
              />
              <p>{label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default React.memo(UInsiderChart);
